/**
 * Real self-fetching ESS invite management hook (#23314). Mirrors
 * `use_leave_requests.ts`'s established pattern: the real bearer client
 * (`getTrpcClient()`, `lib/trpc.ts`), the shared `useOAuthEntityContext()`
 * helper, DI-testable fetch/mutation functions, an explicit state machine
 * (`loading` / `no_entity` / `entitlement_required` / `error` / `ready`), no
 * mock fallback anywhere.
 *
 * ── Two real reads, one shared entity context ──────────────────────────
 *
 *  - `payroll.listEssInvites` — invites entity-wide. The per-employee slice
 *    is derived client-side via `mostRecentInviteForEmployee`, so a single
 *    fetch feeds every employee's profile access-tab.
 *  - `payroll.listEmployees` — resolves `employee_id` → display name AND
 *    supplies the per-employee `employee_user_id` used to distinguish an
 *    ACCEPTED-then-revoked-invite (still linked) from a revoked-before-
 *    acceptance (not linked). Same read `use_leave_requests.ts`/`use_expenses.ts`
 *    already reuse.
 *
 * `deriveEssInvitesFromRecords` (`wagepro_store_ess_invites.ts`) is the pure
 * adapter both feed into.
 *
 * ── Three write actions ────────────────────────────────────────────────
 *
 * `create`/`revoke_invite`/`revoke_access` are `undefined` (hiding the
 * affordance entirely, never a button that would 403) unless BOTH of:
 *
 *   - the OAuth entity context resolved (the list itself need not have
 *     loaded — callers gate their buttons on a known status), AND
 *   - `isWriteCapableGrant(grant)` (`use_pay_runs.ts`) — a read-only OAuth
 *     token never sees the buttons.
 *
 * The write path never throws — every failure mode resolves a classified
 * `EssActionFailure` bucket so the UI can render an actionable inline error
 * instead of a raw toast. `no_email`/`already_linked` come from the two
 * BAD_REQUEST branches of `createEssInvite` (`payroll.ts:10262-10274`);
 * `not_found` from the employee/invite lookups; `forbidden` from anything
 * else classified by `isForbiddenTrpcError`.
 *
 * Every successful mutation calls `retry()` to re-fetch, mirroring the
 * `useLeaveRequests`/`use_pay_runs` pattern.
 */
import * as React from 'react';
import {
  getTrpcClient,
  isBadRequestTrpcError,
  isForbiddenTrpcError,
  isNotFoundTrpcError,
  isPayrollAddonRequiredError,
  PAYROLL_CONTRACTOR_NOT_ELIGIBLE_CODE,
  workerTypeErrorCode,
} from '../lib/trpc.js';
import { useAuth } from '../lib/auth/auth_context.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import { isWriteCapableGrant } from './use_pay_runs.js';
import {
  deriveEssInvitesFromRecords,
  deriveEssStatusForEmployee,
  mostRecentInviteForEmployee,
  type EssEmployeeStatus,
  type EssInvite,
  type RealEssInviteRecord,
} from './wagepro_store_ess_invites.js';
import type { EssEmployeeLookup } from './types_ess_invites.js';

export interface EssInvitesFetchInput {
  firm_id: string;
  entity_id: string;
}

export interface EssInvitesFetchDeps {
  listInvites: (
    input: EssInvitesFetchInput,
  ) => Promise<{ invites: RealEssInviteRecord[] }>;
  listEmployees: (
    input: EssInvitesFetchInput,
  ) => Promise<{ employees: EssEmployeeLookup[] }>;
}

function defaultEssInvitesFetchDeps(): EssInvitesFetchDeps {
  const client = getTrpcClient();
  return {
    listInvites: (input) => client.payroll.listEssInvites.query(input),
    listEmployees: (input) => client.payroll.listEmployees.query(input),
  };
}

/** How the bundle can fail. `entitlement_required` is classified against the payroll add-on gate; everything else is generic `error`. */
export type EssLoadFailure = 'entitlement_required' | 'error';

function classifyLoadFailure(err: unknown): EssLoadFailure {
  return isPayrollAddonRequiredError(err) ? 'entitlement_required' : 'error';
}

export type EssInvitesFetchResult =
  | {
      outcome: 'success';
      invites: EssInvite[];
      employees: EssEmployeeLookup[];
    }
  | { outcome: 'failure'; failure: EssLoadFailure };

/**
 * Resolves the entity's invite list joined against the employee directory,
 * or classifies why it couldn't. Exported and DI'd so the classification is
 * unit-testable against a fake client, mirroring `fetchLeaveRequestsBundle`.
 */
export async function fetchEssInvitesBundle(
  input: EssInvitesFetchInput,
  deps?: EssInvitesFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<EssInvitesFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultEssInvitesFetchDeps();
    const [{ invites }, { employees }] = await Promise.all([
      resolvedDeps.listInvites(input),
      resolvedDeps.listEmployees(input),
    ]);
    const derived = deriveEssInvitesFromRecords(invites, employees);
    return { outcome: 'success', invites: derived, employees: [...employees] };
  } catch (err) {
    if (!isCancelled()) {
      console.error(
        '[wagepro-web] Failed to resolve the real ESS invite list:',
        err,
      );
    }
    return { outcome: 'failure', failure: classifyLoadFailure(err) };
  }
}

export type EssState =
  | { status: 'loading' }
  | { status: 'no_entity' }
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | {
      status: 'ready';
      invites: EssInvite[];
      employees: EssEmployeeLookup[];
    };

export function resolveEssState(input: {
  entityContext: OAuthEntityContext;
  invites: EssInvite[] | undefined;
  employees: EssEmployeeLookup[] | undefined;
  failure: EssLoadFailure | undefined;
}): EssState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.invites !== undefined && input.employees !== undefined) {
    return {
      status: 'ready',
      invites: input.invites,
      employees: input.employees,
    };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

// ── Write path: create / revoke_invite / revoke_access ────────────────────

/**
 * How a `createEssInvite`/`revokeEssInvite`/`revokeEssAccess` mutation can
 * fail, classified so the UI can render an inline "what to do next" message
 * rather than a raw toast. `no_email`/`already_linked` come from the two
 * BAD_REQUEST branches of `createEssInvite`; `not_found` from the employee
 * or invite lookup; `forbidden` catches missing scope / non-admin firm role.
 */
export type EssActionFailure =
  | 'entitlement_required'
  | 'no_email'
  | 'already_linked'
  | 'not_found'
  | 'forbidden'
  /** `BAD_REQUEST` + `LEDGER_PAYROLL_CONTRACTOR_NOT_ELIGIBLE` -- self-service is for employees, never contractors. */
  | 'contractor_not_eligible'
  | 'error';

const NO_EMAIL_MESSAGE = 'Employee has no email address to invite';
const ALREADY_LINKED_MESSAGE = 'Employee is already linked to a user account';

/** Exported for unit tests. */
export function classifyActionFailure(err: unknown): EssActionFailure {
  if (isPayrollAddonRequiredError(err)) return 'entitlement_required';
  if (workerTypeErrorCode(err) === PAYROLL_CONTRACTOR_NOT_ELIGIBLE_CODE) {
    return 'contractor_not_eligible';
  }
  if (isForbiddenTrpcError(err)) return 'forbidden';
  if (isNotFoundTrpcError(err)) return 'not_found';
  if (isBadRequestTrpcError(err)) {
    const message = err instanceof Error ? err.message : '';
    if (message === NO_EMAIL_MESSAGE) return 'no_email';
    if (message === ALREADY_LINKED_MESSAGE) return 'already_linked';
    return 'error';
  }
  return 'error';
}

/**
 * `payroll.createEssInvite`'s result, discriminated on `email_sent`.
 *
 *  - `email_sent: true` — the invitation (set-password link + one-time app
 *    code) is in the employee's inbox. The server returns NO credential: the
 *    employer only needs to know where it went.
 *  - `email_sent: false` — the send failed (fail-open server-side: the invite
 *    row is committed). Only here does the server return the raw link + token,
 *    so the employer can hand it over another way. The UI must not claim an
 *    email was sent in this case.
 *
 * The one-time code is never returned in either case.
 */
export type EssCreateSuccess =
  | {
      email_sent: true;
      /** The address the invite is bound to — where the email went. */
      invited_email: string;
    }
  | EssCreateLinkFallback;

/** The email-failed branch: the one-time link the employer must deliver. */
export interface EssCreateLinkFallback {
  email_sent: false;
  invited_email: string;
  invite_token: string;
  invite_link: string;
}

export type EssCreateResult =
  | { outcome: 'success'; value: EssCreateSuccess }
  | { outcome: 'failure'; failure: EssActionFailure };

export type EssActionResult =
  | { outcome: 'success' }
  | { outcome: 'failure'; failure: EssActionFailure };

export interface EssActionDeps {
  createEssInvite: (
    input: EssInvitesFetchInput & { employee_id: string },
  ) => Promise<EssCreateSuccess>;
  revokeEssInvite: (
    input: EssInvitesFetchInput & { invite_id: string },
  ) => Promise<unknown>;
  revokeEssAccess: (
    input: EssInvitesFetchInput & { employee_id: string },
  ) => Promise<unknown>;
}

function defaultEssActionDeps(): EssActionDeps {
  const client = getTrpcClient();
  return {
    createEssInvite: (input) => client.payroll.createEssInvite.mutate(input),
    revokeEssInvite: (input) => client.payroll.revokeEssInvite.mutate(input),
    revokeEssAccess: (input) => client.payroll.revokeEssAccess.mutate(input),
  };
}

/** Calls the real `payroll.createEssInvite`. Never throws. */
export async function createEssInviteRecord(
  employee_id: string,
  entityInput: EssInvitesFetchInput,
  deps?: EssActionDeps,
): Promise<EssCreateResult> {
  try {
    const resolvedDeps = deps ?? defaultEssActionDeps();
    const value = await resolvedDeps.createEssInvite({
      ...entityInput,
      employee_id,
    });
    return { outcome: 'success', value };
  } catch (err) {
    console.error('[wagepro-web] ess: createEssInvite failed:', err);
    return { outcome: 'failure', failure: classifyActionFailure(err) };
  }
}

/** Calls the real `payroll.revokeEssInvite`. Never throws. */
export async function revokeEssInviteRecord(
  invite_id: string,
  entityInput: EssInvitesFetchInput,
  deps?: EssActionDeps,
): Promise<EssActionResult> {
  try {
    const resolvedDeps = deps ?? defaultEssActionDeps();
    await resolvedDeps.revokeEssInvite({ ...entityInput, invite_id });
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] ess: revokeEssInvite failed:', err);
    return { outcome: 'failure', failure: classifyActionFailure(err) };
  }
}

/** Calls the real `payroll.revokeEssAccess`. Never throws. */
export async function revokeEssAccessRecord(
  employee_id: string,
  entityInput: EssInvitesFetchInput,
  deps?: EssActionDeps,
): Promise<EssActionResult> {
  try {
    const resolvedDeps = deps ?? defaultEssActionDeps();
    await resolvedDeps.revokeEssAccess({ ...entityInput, employee_id });
    return { outcome: 'success' };
  } catch (err) {
    console.error('[wagepro-web] ess: revokeEssAccess failed:', err);
    return { outcome: 'failure', failure: classifyActionFailure(err) };
  }
}

export interface UseEssInvitesResult {
  state: EssState;
  invites: EssInvite[];
  employees: EssEmployeeLookup[];
  is_loading: boolean;
  retry: () => void;
  /** Latest invite for one employee, or `undefined` if never invited. Pure — same fn from the store. */
  invite_for_employee: (employee_id: string) => EssInvite | undefined;
  /** Discrete UI status for one employee — combines invites + `employee_user_id`. */
  status_for_employee: (employee_id: string) => EssEmployeeStatus;
  /** `undefined` when the write path isn't reachable (read-only grant / not-ready state). */
  create_invite:
    | ((employee_id: string) => Promise<EssCreateResult>)
    | undefined;
  revoke_invite: ((invite_id: string) => Promise<EssActionResult>) | undefined;
  revoke_access:
    | ((employee_id: string) => Promise<EssActionResult>)
    | undefined;
}

export function useEssInvites(): UseEssInvitesResult {
  const entityContext = useOAuthEntityContext();
  const { grant } = useAuth();
  const [invites, setInvites] = React.useState<EssInvite[] | undefined>(
    undefined,
  );
  const [employees, setEmployees] = React.useState<
    EssEmployeeLookup[] | undefined
  >(undefined);
  const [failure, setFailure] = React.useState<EssLoadFailure | undefined>(
    undefined,
  );
  const [reloadToken, setReloadToken] = React.useState(0);
  /** `firm/entity` whose rows are currently in state — see the load effect. */
  const loadedEntityRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    let cancelled = false;
    // Only a new entity invalidates what's on screen. A same-entity refetch
    // (after an invite/revoke) keeps the current rows until the fresh ones
    // land, rather than blanking every status back to a loading state.
    const entityKey = `${firmId}/${entityId}`;
    if (loadedEntityRef.current !== entityKey) {
      setInvites(undefined);
      setEmployees(undefined);
    }
    setFailure(undefined);

    async function load(): Promise<void> {
      const fetched = await fetchEssInvitesBundle(
        { firm_id: firmId, entity_id: entityId },
        undefined,
        () => cancelled,
      );
      if (cancelled) return;
      if (fetched.outcome === 'success') {
        loadedEntityRef.current = entityKey;
        setInvites(fetched.invites);
        setEmployees(fetched.employees);
      } else {
        setFailure(fetched.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken]);

  const state = resolveEssState({
    entityContext,
    invites,
    employees,
    failure,
  });

  const retry = React.useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  // Writes need only the entity + a write-capable grant, NOT a loaded list:
  // the add-employee auto-invite fires right after a create, often while the
  // list is mid-refetch, and must not silently degrade to "not sent" then.
  const readyEntity = entityContext.status === 'ready' ? entityContext : null;
  const canWrite = readyEntity !== null && isWriteCapableGrant(grant);

  const create_invite = canWrite
    ? (employee_id: string): Promise<EssCreateResult> =>
        createEssInviteRecord(employee_id, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        }).then((actionResult) => {
          if (actionResult.outcome === 'success') retry();
          return actionResult;
        })
    : undefined;

  const revoke_invite = canWrite
    ? (invite_id: string): Promise<EssActionResult> =>
        revokeEssInviteRecord(invite_id, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        }).then((actionResult) => {
          if (actionResult.outcome === 'success') retry();
          return actionResult;
        })
    : undefined;

  const revoke_access = canWrite
    ? (employee_id: string): Promise<EssActionResult> =>
        revokeEssAccessRecord(employee_id, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        }).then((actionResult) => {
          if (actionResult.outcome === 'success') retry();
          return actionResult;
        })
    : undefined;

  const readyInvites = state.status === 'ready' ? state.invites : [];
  const readyEmployees = state.status === 'ready' ? state.employees : [];

  return {
    state,
    invites: readyInvites,
    employees: readyEmployees,
    is_loading: state.status === 'loading',
    retry,
    invite_for_employee: (employee_id: string) =>
      mostRecentInviteForEmployee(readyInvites, employee_id),
    status_for_employee: (employee_id: string) => {
      const emp = readyEmployees.find((e) => e.id === employee_id);
      const employeeUserLinked =
        emp?.employee_user_id !== null && emp?.employee_user_id !== undefined;
      // `undefined` = the lookup didn't carry the field → unknown, not missing.
      const employeeHasEmail =
        emp?.email === undefined ? true : (emp.email ?? '').trim() !== '';
      return deriveEssStatusForEmployee({
        invites: readyInvites,
        employee_id,
        employeeUserLinked,
        employeeHasEmail,
      });
    },
    create_invite,
    revoke_invite,
    revoke_access,
  };
}
